/**
 * Typed fetch helpers for every HTTP endpoint the server exposes (docs/ARCHITECTURE.md §8). Every response
 * is parsed with the shared zod DTO so a shape mismatch fails loudly instead of propagating `unknown` data.
 */
import { z } from 'zod';
import {
  CreateSessionRequest,
  CreateSessionResponse,
  ErrorResponse,
  FixturesResponse,
  InviteDetails,
  InviteInfo,
  JoinResponse,
  ModulesResponse,
  PresenterCreateRequest,
  PresenterCreateResponse,
  VoiceCredentialsResponse,
  type CreateSessionRequestInput,
  type JoinRequestInput,
  type PresenterCreateRequestInput,
  type RejectCode,
} from '@momentum/shared';

const apiBase = import.meta.env.VITE_API_BASE ?? '';

export class ApiError extends Error {
  readonly code: RejectCode;
  constructor(code: RejectCode, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

async function requestJson<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${apiBase}${path}`, {
      ...init,
      headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
    });
  } catch {
    throw new ApiError('internal', 'Could not reach the server');
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    // Some responses (e.g. network hiccups) may have no body; fall through to status handling.
  }

  if (!res.ok) {
    const parsedError = ErrorResponse.safeParse(json);
    if (parsedError.success) throw new ApiError(parsedError.data.code, parsedError.data.message);
    throw new ApiError('internal', `Request failed with status ${res.status}`);
  }

  const parsed = schema.safeParse(json);
  if (!parsed.success) throw new ApiError('internal', 'Unexpected response shape from server');
  return parsed.data;
}

function authHeader(participantToken: string): Record<string, string> {
  return { authorization: `Bearer ${participantToken}` };
}

export function createSession(input: CreateSessionRequestInput, previousHostToken?: string): Promise<CreateSessionResponse> {
  const body = CreateSessionRequest.parse(input);
  return requestJson('/api/sessions', CreateSessionResponse, { method: 'POST', body: JSON.stringify(body), ...(previousHostToken ? { headers: authHeader(previousHostToken) } : {}) });
}

export function getInviteInfo(inviteToken: string): Promise<InviteInfo> {
  return requestJson(`/api/invites/${encodeURIComponent(inviteToken)}`, InviteInfo);
}

export function resolveShortCode(code: string): Promise<InviteInfo> {
  return requestJson('/api/invites/resolve', InviteInfo, { method: 'POST', body: JSON.stringify({ code }) });
}

export function joinSession(inviteToken: string, input: JoinRequestInput): Promise<JoinResponse> {
  return requestJson(`/api/invites/${encodeURIComponent(inviteToken)}/join`, JoinResponse, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function getInviteDetails(sessionId: string, participantToken: string): Promise<InviteDetails> {
  return requestJson(`/api/sessions/${encodeURIComponent(sessionId)}/invite`, InviteDetails, {
    headers: authHeader(participantToken),
  });
}

export function getVoiceCredentials(sessionId: string, participantToken: string): Promise<z.infer<typeof VoiceCredentialsResponse>> {
  return requestJson(`/api/sessions/${encodeURIComponent(sessionId)}/voice`, VoiceCredentialsResponse, {
    headers: authHeader(participantToken),
  });
}

export function getModules(): Promise<z.infer<typeof ModulesResponse>> {
  return requestJson('/api/modules', ModulesResponse);
}

export function getFixtures(): Promise<z.infer<typeof FixturesResponse>> {
  return requestJson('/api/fixtures', FixturesResponse);
}

export function createPresenterSession(input: PresenterCreateRequestInput): Promise<z.infer<typeof PresenterCreateResponse>> {
  const body = PresenterCreateRequest.parse(input);
  return requestJson('/api/presenter/sessions', PresenterCreateResponse, { method: 'POST', body: JSON.stringify(body) });
}

const DemoPlayersAdded = z.object({ addedIds: z.array(z.string()) });
const DemoPlayersRemoved = z.object({ removedIds: z.array(z.string()) });

export function addDemoPlayers(sessionId: string, participantToken: string): Promise<z.infer<typeof DemoPlayersAdded>> {
  return requestJson(`/api/sessions/${encodeURIComponent(sessionId)}/demo-players`, DemoPlayersAdded, {
    method: 'POST',
    headers: authHeader(participantToken),
  });
}

export function removeDemoPlayers(sessionId: string, participantToken: string): Promise<z.infer<typeof DemoPlayersRemoved>> {
  return requestJson(`/api/sessions/${encodeURIComponent(sessionId)}/demo-players`, DemoPlayersRemoved, {
    method: 'DELETE',
    headers: authHeader(participantToken),
  });
}

/**
 * The session's own origin, `ws(s)://host/ws` — matches the Vite dev proxy (`/ws` -> the API server) and a
 * production build served from the same origin as the API. We deliberately don't reuse the absolute `wsUrl`
 * from create/join responses so a page reload always reconnects through whatever origin served this page.
 */
export function deriveWsUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}
